import { ReactElement } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { WARN_THRESHOLD_USERS } from '../constants'

interface BulkSendWarningProps {
    /** How many people this send will email. */
    count: number
    /** Account the batch belongs to; always one, since a send is scoped to the selected account. */
    accountName?: string
    /** Question line ("Do you want to continue?"). Omitted where the dialog's own buttons ask it. */
    showContinuePrompt?: boolean
}

/**
 * UBQ-5212: the deliverability warning the ticket mandates before any bulk send.
 *
 * <p>Shown unconditionally, not above a threshold — the acceptance criteria require the warning and the
 * counts on every send, and list "warn more strongly past N" as an optional extra rather than as the
 * trigger for the base warning. Past {@link WARN_THRESHOLD_USERS} the count line is emphasised, which is
 * that optional extra.
 *
 * <p>Rendered with `component="div"` throughout: both call sites put this inside
 * {@code ConfirmationDialog}, whose body sits in a {@code DialogContentText} — a {@code <p>} — and a
 * {@code <div>}/{@code <p>} nested in a {@code <p>} is a DOM-nesting warning.
 */
const BulkSendWarning = ({
    count,
    accountName,
    showContinuePrompt = false,
}: BulkSendWarningProps): ReactElement => {
    const { t } = useTranslation()
    const isLarge = count > WARN_THRESHOLD_USERS

    return (
        <Box component="div">
            <Typography component="div" sx={{ fontSize: 14, fontWeight: 600 }}>
                {t('bulkSendWarningTitle')}
            </Typography>
            <Typography
                component="div"
                sx={{ fontSize: 14, mt: 1, opacity: 0.85 }}
            >
                {t('bulkSendWarningBody')}
            </Typography>
            <Typography
                component="div"
                color={isLarge ? 'warning.main' : undefined}
                sx={{ fontSize: 14, mt: 1.5, fontWeight: isLarge ? 600 : 500 }}
            >
                {t('bulkSendWarningCount', {
                    count,
                    account: accountName ?? '',
                })}
            </Typography>
            {showContinuePrompt && (
                <Typography component="div" sx={{ fontSize: 14, mt: 1.5 }}>
                    {t('bulkSendWarningContinue')}
                </Typography>
            )}
        </Box>
    )
}

export default BulkSendWarning
